import { SkyButton, SkyDropdown, SkyText } from "@skypointcloud/skypoint-ui";
import { useId } from "react";
import { ArrowUpIcon, ChevronLeftIcon, ChevronRightIcon } from "@/lib/icons";

/**
 * Round 4 (owner, 2026-09-30, items 10a and 10c; rules.md §4: "offer 10/20/50; always show 'Rows
 * per page' + total count"): the one pager of a server-paged list on a keyset cursor, under the
 * Runs list, a connection's imports and a run's events.
 *
 * `SkyPagination` is not used: it needs a page count and offers "Go to page", and a keyset cursor
 * cannot jump (it names the rows after one row, never a page number). So this is Previous and
 * Next over the cursors the server issued (`useCursorPager`), "Rows per page", and where the
 * reader is: "21-40 of 143".
 *
 * Previous and Next are `aria-disabled` with the click swallowed, never natively disabled: the
 * reader who pressed Next onto the last page keeps focus on Next instead of dropping to the page
 * body. While the next page is being read, Next says so (`aria-busy`) and a second press does
 * nothing.
 */
export interface CursorPagerProps {
  /** The nav's test id, and the prefix of its parts (`-range`, `-size`, `-previous`, `-next`). */
  testId: string;
  /** The nav's accessible name, "Runs pages". */
  label: string;
  /** What the rows are, plural, for the buttons' names: "Next page of runs". */
  noun: string;
  /** The first and last row on screen, 1-based (`pagerRange`); 0 and 0 with none. */
  start: number;
  end: number;
  /** The whole filtered list (`meta.total`); null when the server did not say. */
  total: number | null | undefined;
  pageSize: number;
  pageSizes: readonly number[];
  onPageSizeChange: (size: number) => void;
  hasPrevious: boolean;
  onPrevious: () => void;
  hasNext: boolean;
  onNext: () => void;
  nextPending?: boolean;
  className?: string;
}

export function CursorPager({
  testId,
  label,
  noun,
  start,
  end,
  total,
  pageSize,
  pageSizes,
  onPageSizeChange,
  hasPrevious,
  onPrevious,
  hasNext,
  onNext,
  nextPending = false,
  className,
}: CursorPagerProps) {
  const sizeLabelId = useId();
  const range = start > 0 ? `${start}-${end}` : "0";
  const known = typeof total === "number";
  const nextOff = !hasNext || nextPending;
  return (
    <nav
      aria-label={label}
      className={`flex flex-wrap items-center gap-4 ${className ?? ""}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2">
        <SkyText id={sizeLabelId} size={200} color="muted">
          Rows per page
        </SkyText>
        <div className="w-20">
          <SkyDropdown
            options={pageSizes.map((size) => ({ value: String(size), text: String(size) }))}
            selectedOptions={[String(pageSize)]}
            onOptionSelect={(values) => {
              const picked = Number(values[0]);
              if (Number.isFinite(picked)) onPageSizeChange(picked);
            }}
            ariaLabel="Rows per page"
            size="small"
            testId={`${testId}-size`}
          />
        </div>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <SkyText size={200} color="muted" font="numeric" testId={`${testId}-range`}>
          {known ? `${range} of ${total}` : range}
        </SkyText>
        <SkyButton
          appearance="subtle"
          size="small"
          icon={ChevronLeftIcon}
          aria-label={`Previous page of ${noun}`}
          aria-disabled={hasPrevious ? undefined : true}
          className={hasPrevious ? undefined : "cursor-not-allowed opacity-50"}
          onClick={() => {
            if (hasPrevious) onPrevious();
          }}
          testId={`${testId}-previous`}
        >
          Previous
        </SkyButton>
        <SkyButton
          appearance="secondary"
          size="small"
          icon={ChevronRightIcon}
          aria-label={`Next page of ${noun}`}
          aria-disabled={nextOff ? true : undefined}
          aria-busy={nextPending ? true : undefined}
          className={nextOff ? "cursor-not-allowed opacity-50" : undefined}
          onClick={() => {
            if (!nextOff) onNext();
          }}
          testId={`${testId}-next`}
        >
          Next
        </SkyButton>
      </div>
    </nav>
  );
}

/**
 * On a later page, the rows that arrived above the reader since they left the first page (item
 * 10a): the page they are reading never shifts under them, and this one button takes them back to
 * the newest. Nothing while none arrived.
 */
export function NewerRowsNotice({
  count,
  one,
  other,
  onBack,
  testId,
}: {
  count: number;
  one: string;
  other: string;
  onBack: () => void;
  testId: string;
}) {
  if (count <= 0) return null;
  return (
    <SkyButton
      appearance="secondary"
      size="small"
      icon={ArrowUpIcon}
      onClick={onBack}
      testId={testId}
    >
      {`${count} newer ${count === 1 ? one : other}, back to newest`}
    </SkyButton>
  );
}
